import type { DatabaseEngineId } from '@/core/engines';

import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { DatabaseObjectTreeNode, DatabaseObjectNodeMetadata } from '../../explorer/models/DatabaseTreeModels';
import { getConnectionById, getEngineForConnection } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { openDataCompareAsSource, openDataCompareAsTarget } from '../../workspace/data-compare/services/dataCompareNavigation';
import { openDataProfileTab } from '../../workspace/data-profile/services/dataProfileNavigation';
import { openReferencesTab } from '../../navigational-features/find-references/openReferencesTab';
import { buildOeRelationIdentifier } from '../../navigational-features/oeIdentifier';
import { useRenamePreviewStore } from '../../navigational-features/rename/renamePreviewStore';
import { trackNavFeature } from '../../navigational-features/analytics/trackNavFeature';
import { openMigrateTableTab } from '../../cross-engine-migrate/openMigrateTab';
import { openDataImportTab } from '../../workspace/data-import/openDataImportTab';
import { supportsDataImportEngine } from '../../workspace/data-import/mapping/targetEligibility';
import type { ImportTargetPlan } from '../../../types/electron';
import {
  executeAction,
  type ObjectActionContext,
} from '../../shared/object-actions';
import {
  parseNodeId,
  isPinnedDescendantNodeId,
  canonicalLeafIdFromPinnedDescendantId,
} from '../../explorer/utils/nodeIdUtils';
import { engineSupports } from '@/core/engines';
import { fetchServerVersionInfo } from '../../connections/services/serverInfoService';
import { parsePgMajorVersion } from '../../workspace/common/hooks/usePgServerMajorVersion';
import {
  generatePgsqlMaintenanceScript,
  type TableMaintenanceAction,
} from './tableMaintenanceScripts';
import { handleViewOnMap } from './tableViewOnMapAction';
import type { AiTaskContext } from '../../ai-chat/launchers/launcherRegistry';
import { createAiSubmenu } from './aiSubmenuBuilder';
import { isBetaFeatureEnabled } from '../../../core/beta';
import { SELECT_TOP_ROW_LIMIT } from '../../scripting/selectTopRowLimit';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

/**
 * Table-specific context menu action types
 */
type TableContextMenuActionType =
  | 'refresh'
  | 'script-table-as'
  | 'script-table-create'
  | 'script-table-drop'
  | 'script-table-select'
  | 'script-table-insert'
  | 'script-table-update'
  | 'script-table-delete'
  | 'select-top-1000'
  | 'edit-top-100'
  | 'design-table'
  | 'clone-table'
  | 'data-compare'
  | 'data-compare-set-source'
  | 'data-compare-set-target'
  | 'migrate-table'
  | 'import-data-into-table'
  | 'show-dependencies'
  | 'profile-table'
  | 'find-references'
  | 'rename-table'
  | 'maintenance-vacuum'
  | 'maintenance-analyze'
  | 'maintenance-repack'
  | 'maintenance-repack-concurrently'
  | 'view-on-map';

const TableActions = {
  REFRESH: 'refresh' as TableContextMenuActionType,
  SCRIPT_TABLE_AS: 'script-table-as' as TableContextMenuActionType,
  SCRIPT_TABLE_CREATE: 'script-table-create' as TableContextMenuActionType,
  SCRIPT_TABLE_DROP: 'script-table-drop' as TableContextMenuActionType,
  SCRIPT_TABLE_SELECT: 'script-table-select' as TableContextMenuActionType,
  SCRIPT_TABLE_INSERT: 'script-table-insert' as TableContextMenuActionType,
  SCRIPT_TABLE_UPDATE: 'script-table-update' as TableContextMenuActionType,
  SCRIPT_TABLE_DELETE: 'script-table-delete' as TableContextMenuActionType,
  SELECT_TOP_1000: 'select-top-1000' as TableContextMenuActionType,
  EDIT_TOP_100: 'edit-top-100' as TableContextMenuActionType,
  DESIGN_TABLE: 'design-table' as TableContextMenuActionType,
  CLONE_TABLE: 'clone-table' as TableContextMenuActionType,
  DATA_COMPARE: 'data-compare' as TableContextMenuActionType,
  DATA_COMPARE_SET_SOURCE: 'data-compare-set-source' as TableContextMenuActionType,
  DATA_COMPARE_SET_TARGET: 'data-compare-set-target' as TableContextMenuActionType,
  MIGRATE_TABLE: 'migrate-table' as TableContextMenuActionType,
  IMPORT_DATA_INTO_TABLE: 'import-data-into-table' as TableContextMenuActionType,
  SHOW_DEPENDENCIES: 'show-dependencies' as TableContextMenuActionType,
  PROFILE_TABLE: 'profile-table' as TableContextMenuActionType,
  FIND_REFERENCES: 'find-references' as TableContextMenuActionType,
  RENAME_TABLE: 'rename-table' as TableContextMenuActionType,
  MAINTENANCE_VACUUM: 'maintenance-vacuum' as TableContextMenuActionType,
  MAINTENANCE_ANALYZE: 'maintenance-analyze' as TableContextMenuActionType,
  MAINTENANCE_REPACK: 'maintenance-repack' as TableContextMenuActionType,
  MAINTENANCE_REPACK_CONCURRENTLY: 'maintenance-repack-concurrently' as TableContextMenuActionType,
  VIEW_ON_MAP: 'view-on-map' as TableContextMenuActionType,
};

/**
 * Mapping from Object Explorer action IDs to shared action IDs
 */
const actionIdMapping: Record<string, string> = {
  [TableActions.SELECT_TOP_1000]: 'select-top-1000',
  [TableActions.EDIT_TOP_100]: 'edit-top-200', // Maps to shared action with 200 limit
  // Kusto read-only browse: same shared TE-open action, no edit affordance.
  'open-table-explorer': 'edit-top-200',
  [TableActions.DESIGN_TABLE]: 'design',
  [TableActions.SCRIPT_TABLE_CREATE]: 'script-create',
  [TableActions.SCRIPT_TABLE_DROP]: 'script-drop',
  [TableActions.SHOW_DEPENDENCIES]: 'view-dependencies',
};

/**
 * Create a separator item
 */
export function createSeparator(): ContextMenuAction {
  return {
    id: 'separator',
    label: '',
    isSeparator: true
  };
}

/**
 * Create a refresh action
 */
export function createRefreshAction(): ContextMenuAction {
  return {
    id: TableActions.REFRESH,
    label: 'Refresh',
    icon: 'refresh'
  };
}

/**
 * Create script table submenu
 */
export function createScriptTableSubmenu(): ContextMenuAction {
  return {
    id: TableActions.SCRIPT_TABLE_AS,
    label: 'Script table as',
    icon: 'code',
    submenu: [
      {
        id: TableActions.SCRIPT_TABLE_CREATE,
        label: 'CREATE',
        icon: 'file-plus'
      },
      {
        id: TableActions.SCRIPT_TABLE_DROP,
        label: 'DROP',
        icon: 'trash'
      },
      {
        id: TableActions.SCRIPT_TABLE_SELECT,
        label: 'SELECT',
        icon: 'search'
      },
      {
        id: TableActions.SCRIPT_TABLE_INSERT,
        label: 'INSERT',
        icon: 'plus'
      },
      {
        id: TableActions.SCRIPT_TABLE_UPDATE,
        label: 'UPDATE',
        icon: 'edit'
      },
      {
        id: TableActions.SCRIPT_TABLE_DELETE,
        label: 'DELETE',
        icon: 'delete'
      }
    ]
  };
}

/**
 * Create select top 1000 rows action
 */
export function createSelectTop1000Action(): ContextMenuAction {
  return {
    id: TableActions.SELECT_TOP_1000,
    label: `Select top ${SELECT_TOP_ROW_LIMIT} rows`,
    icon: 'table'
  };
}

/**
 * Create edit top 100 rows action
 */
export function createEditTop100Action(): ContextMenuAction {
  return {
    id: TableActions.EDIT_TOP_100,
    label: 'Edit top 100 rows',
    icon: 'edit'
  };
}

/**
 * Create design table action
 */
export function createDesignTableAction(): ContextMenuAction {
  return {
    id: TableActions.DESIGN_TABLE,
    label: 'Design Table...',
    icon: 'settings'
  };
}

/**
 * Create clone table action
 */
export function createCloneTableAction(): ContextMenuAction {
  return {
    id: TableActions.CLONE_TABLE,
    label: 'Clone Table...',
    icon: 'copy'
  };
}

/**
 * Create data compare submenu
 */
export function createDataCompareSubmenu(): ContextMenuAction {
  return {
    id: TableActions.DATA_COMPARE,
    label: 'Data Compare',
    icon: 'diff',
    submenu: [
      {
        id: TableActions.DATA_COMPARE_SET_SOURCE,
        label: 'Set as Source',
        icon: 'arrow-right'
      },
      {
        id: TableActions.DATA_COMPARE_SET_TARGET,
        label: 'Set as Target',
        icon: 'arrow-left'
      }
    ]
  };
}

/**
 * Create "Migrate Table to Connection…" action — opens the cross-engine
 * migration dialog (copy schema + data to a target connection of any engine).
 */
export function createMigrateTableAction(): ContextMenuAction {
  return {
    id: TableActions.MIGRATE_TABLE,
    label: 'Migrate Table to Connection...',
    icon: 'arrow-right'
  };
}

/**
 * Create "Import Data into Table..." action — opens the Data Import wizard
 * pre-seeded with this table as an existing-table target.
 */
export function createImportDataIntoTableAction(): ContextMenuAction {
  return {
    id: TableActions.IMPORT_DATA_INTO_TABLE,
    label: 'Import Data into Table...',
    icon: 'upload'
  };
}

/**
 * Create show dependencies action
 */
export function createShowDependenciesAction(): ContextMenuAction {
  return {
    id: TableActions.SHOW_DEPENDENCIES,
    label: 'Show Dependencies',
    icon: 'git-branch'
  };
}

/**
 * Create profile table action — opens the Data Profile workspace tab.
 */
export function createProfileTableAction(): ContextMenuAction {
  return {
    id: TableActions.PROFILE_TABLE,
    label: 'Profile data',
    icon: 'bar-chart'
  };
}

/**
 * Create find-references action — opens the NavigationalFeatures
 * Find-References tab listing every persisted object that references the
 * table. Phase-3 Slice 25.
 */
export function createFindReferencesAction(): ContextMenuAction {
  return {
    id: TableActions.FIND_REFERENCES,
    label: 'Find references…',
    icon: 'search',
  };
}

/**
 * Create rename-table action — opens the NavigationalFeatures rename
 * preview dialog with a name-input pre-step. Phase-3 Slice 36.
 */
export function createRenameTableAction(): ContextMenuAction {
  return {
    id: TableActions.RENAME_TABLE,
    label: 'Rename…',
    icon: 'edit',
  };
}

/**
 * Create the PostgreSQL Maintenance submenu (VACUUM / ANALYZE / REPACK).
 * REPACK is PostgreSQL 19+ — the menu always offers it (version-agnostic,
 * like the Property Graphs folder), and the generated script carries a
 * warning comment when the connected server is older or unknown.
 */
export function createMaintenanceSubmenu(): ContextMenuAction {
  return {
    id: 'maintenance',
    label: 'Maintenance',
    icon: 'wrench',
    submenu: [
      { id: TableActions.MAINTENANCE_VACUUM, label: 'Script VACUUM (ANALYZE)' },
      { id: TableActions.MAINTENANCE_ANALYZE, label: 'Script ANALYZE' },
      { id: TableActions.MAINTENANCE_REPACK, label: 'Script REPACK  (PG 19+)' },
      { id: TableActions.MAINTENANCE_REPACK_CONCURRENTLY, label: 'Script REPACK CONCURRENTLY  (PG 19+)' },
    ],
  };
}

/**
 * Create "View on Map" action — opens a Spatial Explorer tab preloaded with this
 * table as layer 1. Shown per-engine (spatial-capable engines); whether the table
 * actually HAS a spatial column isn't knowable at menu-build time, so it's
 * validated on click (see `handleViewOnMap`) — an informational toast when none
 * is found, never a dead workspace.
 */
export function createViewOnMapAction(): ContextMenuAction {
  return {
    id: TableActions.VIEW_ON_MAP,
    label: 'View on Map',
    icon: 'map',
  };
}

/**
 * Generate table-specific context menu actions.
 *
 * Kusto is read-only (append-only) and has no DDL/DML/design/migrate/profile
 * surface, so a kusto table gets only the actions that actually work against a
 * cluster: "Select top 1000 rows" (emits `Table | take 1000` via the kusto
 * script provider), "Copy table name", "AI" (read-only `explain-table` only —
 * `generate-test-data` would emit an `INSERT` that append-only kusto has no
 * surface for, T12 fix round 1), and "Refresh". Every other engine keeps the
 * full menu unchanged.
 *
 * `aiCliAvailable` / `aiCliDetectionLoading` drive the "AI" submenu's disabled
 * state (see `createAiSubmenu` in `aiSubmenuBuilder.ts`) — omitted at call
 * sites that don't render live (e.g. `databaseNodeService.ts`'s tree-build
 * pass), which the AI items render enabled; `TreeNode.tsx`'s render-time call
 * is the one that supplies them.
 */
export function generateTableContextMenuActions(
  engine?: DatabaseEngineId,
  aiCliAvailable?: boolean,
  aiCliDetectionLoading?: boolean,
): ContextMenuAction[] {
  if (engine === 'kusto') {
    return [
      createSelectTop1000Action(),
      // Read-only browse — kusto Table Explorer renders non-editable, so this is
      // "Open in Table Explorer" (not "Edit top N"). Reuses the shared TE-open
      // action via `actionIdMapping` below.
      { id: 'open-table-explorer', label: 'Open in Table Explorer', icon: 'table' },
      createSeparator(),
      { id: 'copy-name', label: 'Copy table name', icon: 'clipboard' },
      createAiSubmenu(['explain-table'], {}, aiCliAvailable, aiCliDetectionLoading),
      createSeparator(),
      createRefreshAction(),
    ];
  }

  const actions: ContextMenuAction[] = [];

  // Primary data actions at the top
  actions.push(createSelectTop1000Action());
  actions.push(createEditTop100Action());
  actions.push(createSeparator());

  // Copy name action
  actions.push({ id: 'copy-name', label: 'Copy table name', icon: 'clipboard' });

  // Remaining table-specific actions
  actions.push(createDesignTableAction());
  actions.push(createScriptTableSubmenu());
  actions.push(createCloneTableAction());
  actions.push(createDataCompareSubmenu());
  if (isBetaFeatureEnabled('cross-engine-migrate')) {
    actions.push(createMigrateTableAction());
  }
  // Kusto returned above; the shared Data Import entry-point gate keeps any other non-target engine out too.
  if (supportsDataImportEngine(engine) && isBetaFeatureEnabled('data-import')) {
    actions.push(createImportDataIntoTableAction());
  }
  actions.push(createShowDependenciesAction());
  actions.push(createProfileTableAction());
  actions.push(createFindReferencesAction());
  actions.push(createRenameTableAction());
  if (engine === 'pgsql') {
    actions.push(createMaintenanceSubmenu());
  }
  if (engineSupports(engine ?? 'mssql', 'spatial', 'supportsSpatialTypes')) {
    actions.push(createViewOnMapAction());
  }
  actions.push(createAiSubmenu(['explain-table', 'generate-test-data'], {}, aiCliAvailable, aiCliDetectionLoading));

  // Add separator before common actions
  actions.push(createSeparator());

  // Add common actions
  actions.push(createRefreshAction());

  return actions;
}

/**
 * Extract database name from parent folder node ID
 * Parent ID format: "tables-folder<->connectionId<->database" or "views-folder<->connectionId<->database"
 */
export function extractDatabaseFromParentId(parentId: string | null | undefined): string | undefined {
  if (!parentId) return undefined;
  // Finding P1 — a table pinned (or living) under a pinned wrapper has a
  // `pinned-desc<->{wrapperId}<->{canonicalFolderId}` parent. Parsing that
  // directly returns the connectionId as parts[2] (the wrapper's own cid),
  // poisoning every context-menu action with the wrong database. Recover the
  // canonical folder id first so parts[2] is the real database again.
  const effectiveParentId = isPinnedDescendantNodeId(parentId)
    ? canonicalLeafIdFromPinnedDescendantId(parentId)
    : parentId;
  if (!effectiveParentId) return undefined;
  const parts = parseNodeId(effectiveParentId);
  // Format: [folderType, connectionId, database]
  if (parts.length >= 3) {
    return parts[2];
  }
  return undefined;
}

/**
 * Build ObjectActionContext from a DatabaseObjectTreeNode
 */
function buildActionContext(node: DatabaseObjectTreeNode): ObjectActionContext | null {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: objectName, schema, connectionId } = objectDetails;

  // Get database from parent folder ID (most reliable) or fall back to metadata
  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  // Get connection info
  const { activeConnection, engine } = getConnectionById(connectionId);

  if (!activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return null;
  }

  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
  const resolvedSchema = schema ?? defaultSchema;
  // Prefer database from parent ID, then metadata, then connection default
  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';

  return {
    connectionId,
    databaseName: dbName,
    schemaName: resolvedSchema,
    objectName,
    objectType: 'table',
    objectId: objectDetails.id,
  };
}

/**
 * Build the `AiTaskContext` for an "AI" submenu action fired from this
 * table's context menu (`explain-table` / `generate-test-data`, dispatched by
 * `handleContextMenuAction` in `DatabaseExplorer.tsx`). Reuses the same
 * connection/database/schema resolution as every other table action.
 */
export function buildTableAiTaskContext(node: DatabaseObjectTreeNode): AiTaskContext | null {
  const context = buildActionContext(node);
  if (!context) return null;
  return {
    connectionId: context.connectionId,
    database: context.databaseName,
    object: { schema: context.schemaName, name: context.objectName, kind: 'table' },
  };
}

/**
 * Handle Data Compare - Set as Source action
 */
function handleDataCompareSetSource(node: DatabaseObjectTreeNode): void {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: tableName, schema, connectionId } = objectDetails;

  // Get database from parent folder ID (most reliable) or fall back to metadata
  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  const { activeConnection, engine } = getConnectionById(connectionId);

  if (!activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
  const resolvedSchema = schema ?? defaultSchema;
  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';

  openDataCompareAsSource({
    connectionId,
    database: dbName,
    schema: resolvedSchema,
    tableName,
  });
}

/**
 * Handle Data Compare - Set as Target action
 */
function handleDataCompareSetTarget(node: DatabaseObjectTreeNode): void {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: tableName, schema, connectionId } = objectDetails;

  // Get database from parent folder ID (most reliable) or fall back to metadata
  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  const { activeConnection, engine } = getConnectionById(connectionId);

  if (!activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
  const resolvedSchema = schema ?? defaultSchema;
  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';

  openDataCompareAsTarget({
    connectionId,
    database: dbName,
    schema: resolvedSchema,
    tableName,
  });
}

/**
 * Handle "Migrate Table to Connection…" — opens the cross-engine migration
 * dialog seeded with this table as the source.
 */
function handleMigrateTable(node: DatabaseObjectTreeNode): void {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: tableName, schema, connectionId } = objectDetails;

  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  const { activeConnection, engine } = getConnectionById(connectionId);
  if (!activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
  const resolvedSchema = schema ?? defaultSchema;
  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';

  // Open the migration as a dedicated workspace tab (a migration can be
  // long-running, so it gets its own surface with live progress + cancel).
  openMigrateTableTab({
    connectionId,
    connectionName: safeConnectionAlias(activeConnection.connection, 'Unnamed Connection'),
    serverHost: safeConnectionServer(activeConnection.connection),
    database: dbName,
    engine: engine ?? 'mssql',
    schema: resolvedSchema,
    table: tableName,
  });
}

/**
 * Handle "Import Data into Table..." — opens the Data Import wizard
 * pre-seeded with this table as an existing-table target.
 */
function handleImportDataIntoTable(node: DatabaseObjectTreeNode): void {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: tableName, schema, connectionId } = objectDetails;

  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  const { activeConnection, engine } = getConnectionById(connectionId);
  if (!activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
  const resolvedSchema = schema ?? defaultSchema;
  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';

  const target: ImportTargetPlan = {
    mode: 'existing-table',
    connectionId,
    database: dbName,
    schema: resolvedSchema,
    table: tableName,
  };
  openDataImportTab({
    target,
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(activeConnection.connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: dbName,
    },
  });
  window.electronAPI?.analytics?.track('feature:data_import_open', { source: 'table_menu' }).catch(() => {
    // analytics is fire-and-forget
  });
}

/**
 * Execute a table context menu action
 *
 * Delegates to the shared object actions system where possible,
 * and handles table-explorer-specific actions locally.
 */
export function executeTableContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: {
    onRefresh?: (nodeId: string) => void;
    onCloneTable?: (
      connectionId: string,
      databaseName: string,
      schema: string,
      tableName: string
    ) => void;
  } = {},
  node?: DatabaseObjectTreeNode,
): void {
  // Handle refresh action
  if (actionId === TableActions.REFRESH) {
    handlers.onRefresh?.(nodeId);
    return;
  }

  // Check if this action maps to a shared action
  const sharedActionId = actionIdMapping[actionId];
  if (sharedActionId && node) {
    const context = buildActionContext(node);
    if (context) {
      void executeAction(sharedActionId, context);
    }
    return;
  }

  // Handle actions that need custom execution (script types not in shared system)
  // SELECT/UPDATE/DELETE scripts are handled via the shared script provider but
  // aren't defined as shared actions (they're table-specific scripts)
  if (node) {
    const context = buildActionContext(node);
    if (!context) return;

    switch (actionId) {
      case TableActions.SCRIPT_TABLE_SELECT:
      case TableActions.SCRIPT_TABLE_INSERT:
      case TableActions.SCRIPT_TABLE_UPDATE:
      case TableActions.SCRIPT_TABLE_DELETE:
        // These use the shared scripting infrastructure but aren't defined as shared actions
        // For now, delegate to a helper (or implement inline)
        void handleTableScript(node, actionId);
        return;

      case TableActions.CLONE_TABLE:
        if (handlers.onCloneTable) {
          handlers.onCloneTable(
            context.connectionId,
            context.databaseName,
            context.schemaName,
            context.objectName,
          );
        } else {
          toast.error({ title: 'Error', description: 'Clone Table handler not provided' });
        }
        return;

      case TableActions.DATA_COMPARE_SET_SOURCE:
        handleDataCompareSetSource(node);
        return;

      case TableActions.DATA_COMPARE_SET_TARGET:
        handleDataCompareSetTarget(node);
        return;

      case TableActions.MIGRATE_TABLE:
        handleMigrateTable(node);
        return;

      case TableActions.IMPORT_DATA_INTO_TABLE:
        handleImportDataIntoTable(node);
        return;

      case TableActions.PROFILE_TABLE:
        openDataProfileTab({
          connectionId: context.connectionId,
          database: context.databaseName,
          schema: context.schemaName,
          table: context.objectName,
          entryPoint: 'object_explorer_table',
        });
        return;

      case TableActions.FIND_REFERENCES: {
        const engineId = getEngineForConnection(context.connectionId) as DatabaseEngineId;
        openReferencesTab({
          connectionId: context.connectionId,
          database: context.databaseName,
          engineId,
          identifier: buildOeRelationIdentifier(engineId, {
            name: context.objectName,
            schema: context.schemaName,
            database: context.databaseName,
          }),
          identifierKind: 'table',
          trigger: 'context-menu',
        });
        return;
      }

      case TableActions.RENAME_TABLE: {
        const engineId = getEngineForConnection(context.connectionId) as DatabaseEngineId;
        trackNavFeature({ name: 'feature:navfeatures_rename_invoke', props: { kind: 'table', source: 'context-menu' } });
        useRenamePreviewStore.getState().startNameInput({
          connectionId: context.connectionId,
          database: context.databaseName,
          engineId,
          kind: 'table',
          fromIdentifier: buildOeRelationIdentifier(engineId, {
            name: context.objectName,
            schema: context.schemaName,
            database: context.databaseName,
          }),
        });
        return;
      }

      case TableActions.MAINTENANCE_VACUUM:
      case TableActions.MAINTENANCE_ANALYZE:
      case TableActions.MAINTENANCE_REPACK:
      case TableActions.MAINTENANCE_REPACK_CONCURRENTLY:
        void handleMaintenanceScript(context, actionId);
        return;

      case TableActions.VIEW_ON_MAP:
        void handleViewOnMap(context);
        return;
    }
  } else {
    toast.error({ title: 'Error', description: 'Table node not found' });
  }
}

/**
 * Handle table script generation for SELECT/UPDATE/DELETE
 * These aren't defined as shared actions but use the shared scripting infrastructure
 */
async function handleTableScript(
  node: DatabaseObjectTreeNode,
  actionId: string,
): Promise<void> {
  const { getScriptProvider } = await import('../../scripting');
  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');

  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: tableName, schema, connectionId } = objectDetails;

  // Get database from parent folder ID (most reliable) or fall back to metadata
  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  const { activeConnection, connection, engine } = getConnectionById(connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';
  const { addTab, getNextQueryNumber, updateTabContent } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  // Map action to script type
  const scriptTypeMap: Record<string, 'select' | 'insert' | 'update' | 'delete'> = {
    [TableActions.SCRIPT_TABLE_SELECT]: 'select',
    [TableActions.SCRIPT_TABLE_INSERT]: 'insert',
    [TableActions.SCRIPT_TABLE_UPDATE]: 'update',
    [TableActions.SCRIPT_TABLE_DELETE]: 'delete',
  };

  const scriptType = scriptTypeMap[actionId];
  if (!scriptType) {
    toast.error({ title: 'Error', description: 'Unknown script type' });
    return;
  }

  window.electronAPI?.analytics?.track('feature:table_script', { scriptType, engine }).catch(() => {});

  const titleMap = { select: 'SELECT', insert: 'INSERT', update: 'UPDATE', delete: 'DELETE' };
  const scriptTitle = `${tableName}_${titleMap[scriptType]}`;

  // Scripts that need metadata. INSERT needs it too — the provider uses column
  // metadata to filter out identity columns (they're excluded from INSERT
  // templates since the engine generates their values).
  const needsMetadata = (scriptType === 'insert' || scriptType === 'update' || scriptType === 'delete') && !!window.electronAPI?.getTableMetadata;

  const tabId = addTab({
    title: `${scriptTitle}-${queryNumber}`,
    type: 'query',
    content: {
      query: needsMetadata ? `-- Generating ${titleMap[scriptType]} script for ${schema}.${tableName}...\n` : '',
      isGeneratingScript: needsMetadata,
    },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: dbName,
    },
  });

  try {
    let tableMetadata: import('../../scripting').TableMetadata | undefined;

    if (needsMetadata) {
      const result = await window.electronAPI!.getTableMetadata({
        connectionId,
        database: dbName,
        schema,
        tableName,
      });

      if (result.success && result.metadata) {
        tableMetadata = result.metadata;
      }
    }

    const provider = getScriptProvider(engine);
    const script = provider.generateTableScript(scriptType, {
      database: dbName,
      schema,
      tableName,
      metadata: tableMetadata,
    });

    updateTabContent(tabId, {
      query: script,
      isGeneratingScript: false,
    });
  } catch (error) {
    updateTabContent(tabId, {
      query: `-- Error generating script: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
      isGeneratingScript: false,
    });
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate script',
    });
  }
}

/**
 * Map a Maintenance submenu action id to the pure script generator's action type.
 */
const maintenanceActionMap: Record<string, TableMaintenanceAction> = {
  [TableActions.MAINTENANCE_VACUUM]: 'vacuum',
  [TableActions.MAINTENANCE_ANALYZE]: 'analyze',
  [TableActions.MAINTENANCE_REPACK]: 'repack',
  [TableActions.MAINTENANCE_REPACK_CONCURRENTLY]: 'repack-concurrently',
};

/**
 * Generate a PostgreSQL table maintenance script (VACUUM / ANALYZE / REPACK)
 * and open it in a new query tab (index-REBUILD model — never executed
 * directly). REPACK's version-aware warning comment needs the connected
 * server's PG major version, fetched fresh since this runs outside React
 * (mirrors `usePgServerMajorVersion`'s parse step without its query cache).
 */
async function handleMaintenanceScript(
  context: ObjectActionContext,
  actionId: string,
): Promise<void> {
  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');

  const action = maintenanceActionMap[actionId];
  if (!action) return;

  const { connectionId, databaseName, schemaName, objectName } = context;
  const { activeConnection, connection, engine } = getConnectionById(connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const info = await fetchServerVersionInfo(connectionId, 'pgsql', databaseName).catch(() => null);
  const pgMajorVersion = info ? parsePgMajorVersion(info.version) : null;

  const script = generatePgsqlMaintenanceScript(action, schemaName, objectName, pgMajorVersion);

  const { addTab } = useWorkspaceStore.getState();
  addTab({
    title: `${objectName}_${action}`,
    type: 'query',
    content: { query: script },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: databaseName,
    },
  });

  window.electronAPI?.analytics?.track('feature:table_maintenance_script', { engine, action }).catch(() => {});
}
